// "Refuser de signer": the signer gives a reason (required, 500 characters at most) that the
// requester reads. The request then ends as declined.
import { FormField, isolate } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Textarea,
  __css,
  tokens,
} from '@fluentui/react-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shared/hooks/use-notify';
import { useApplyRequest, useDeclineSignature } from './api';
import { errorMessage, type SignatureRequest } from './model';

const useStyles = __css({"body":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"text":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb"}});

const REASON_MAX = 500;

export function DeclineDialog({
  orgId,
  request,
  onClose,
}: {
  orgId: string;
  request: SignatureRequest;
  onClose: () => void;
}) {
  const { t } = useTranslation('signatures');
  const styles = useStyles();
  const notify = useNotify();
  const decline = useDeclineSignature();
  const apply = useApplyRequest(orgId);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const missing = reason.trim() === '';

  const submit = async () => {
    setTouched(true);
    if (missing || decline.isPending) return;
    setFailure(null);
    try {
      const fresh = await decline.mutateAsync({
        orgId,
        requestId: request.id,
        data: { reason: reason.trim() },
      });
      await apply(fresh);
      notify({ intent: 'success', message: t('decline.done') });
      onClose();
    } catch (error) {
      setFailure(errorMessage(t, error));
    }
  };

  return (
    <Dialog
      open
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open && !decline.isPending) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('decline.title', { title: isolate(request.title) })}</DialogTitle>
          <DialogContent>
            <div className={styles.body}>
              <p className={styles.text}>{t('decline.intro')}</p>
              <FormField
                label={t('decline.reasonLabel')}
                hint={t('decline.reasonHint', { max: REASON_MAX })}
                error={touched && missing ? t('decline.reasonRequired') : null}
                required
              >
                <Textarea
                  value={reason}
                  maxLength={REASON_MAX}
                  resize="vertical"
                  onChange={(_event, data) => {
                    setReason(data.value);
                    setFailure(null);
                  }}
                />
              </FormField>
              {failure ? (
                <MessageBar intent="error" layout="multiline" role="alert">
                  <MessageBarBody>{failure}</MessageBarBody>
                </MessageBar>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={decline.isPending}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              appearance="secondary"
              disabledFocusable={decline.isPending}
              onClick={() => {
                void submit();
              }}
            >
              {decline.isPending ? t('decline.sending') : t('decline.confirm')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}


import "/opt/batiflow/app/apps/web/src/features/signatures/decline-dialog.griffel.css";